import { useState, useCallback, useEffect } from "react";
import {
  Button,
  Typography,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  CircularProgress,
} from "@mui/material";
import { FaPlusCircle, FaTrashAlt } from "react-icons/fa"; // Added FaTrashAlt
import COLORS from "../../../../../constants/colors";
import SnackbarAlert from "../../Reusbale/SnackbarAlert";
import { toast } from "react-toastify";
import ImageUploader from "./ImageUploader";
import baseURL from "../../../admin/config";
import MDEditor from "@uiw/react-md-editor";
import "@uiw/react-md-editor/markdown-editor.css";
import { formatNumberWithCommas } from "../../../../Reusable/numberUtils";

// Define the key for localStorage
const CAMPAIGN_DRAFT_KEY = "campaignDraft";

const AddCampaignCard = () => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [goal, setGoal] = useState(""); // formatted goal (with commas)
  const [goalRaw, setGoalRaw] = useState(""); // raw numeric goal (no commas)
  const [description, setDescription] = useState("");
  const [campaignImageFile, setCampaignImageFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  // State to track if the draft has been loaded to prevent saving empty state on mount
  const [isDraftLoaded, setIsDraftLoaded] = useState(false); 

  // --- Utility Functions for Draft Management ---

  // Function to clear the draft from localStorage and form state
  const clearDraft = useCallback(() => {
    localStorage.removeItem(CAMPAIGN_DRAFT_KEY);
    setName("");
    setGoal("");
    setGoalRaw("");
    setDescription("");
    setCampaignImageFile(null); 
    // Clear message for deleting/clearing
    toast.success("Campaign draft deleted. The form is now empty.");
  }, []);

  // Function to load the draft from localStorage
  const loadDraft = useCallback(() => {
    try {
      const draft = localStorage.getItem(CAMPAIGN_DRAFT_KEY);
      if (draft) {
        const parsedDraft = JSON.parse(draft);
        setName(parsedDraft.name || "");
        setGoalRaw(parsedDraft.goalRaw || "");
        setGoal(formatNumberWithCommas(parsedDraft.goalRaw || ""));
        setDescription(parsedDraft.description || "");
        // Clear message for loading
        toast.info("Draft campaign reloaded. Continue editing your last draft.");
        setOpen(true); // Open the dialog immediately if a draft exists
      }
    } catch (e) {
      console.error("Could not load draft from localStorage", e);
      localStorage.removeItem(CAMPAIGN_DRAFT_KEY);
    } finally {
        setIsDraftLoaded(true); // Mark as loaded regardless of success/failure
    }
  }, []);

  // Function to save the current form data to localStorage
  const saveDraft = useCallback(() => {
    if (!isDraftLoaded) return; // Don't save before initial load

    const draft = {
      name,
      goalRaw,
      description,
      // Image file is not saved to localStorage
    };
    
    // Only save if there's *any* content
    if (name || goalRaw || description) {
        localStorage.setItem(CAMPAIGN_DRAFT_KEY, JSON.stringify(draft));
    } else {
        localStorage.removeItem(CAMPAIGN_DRAFT_KEY);
    }
  }, [name, goalRaw, description, isDraftLoaded]);


  // --- Effects for Auto-Load and Auto-Save ---

  // 1. Initial Load Effect: Load draft when component mounts
  useEffect(() => {
    loadDraft();
  }, [loadDraft]);

  // 2. Auto-Save Effect: Save draft whenever key state variables change (with debounce)
  useEffect(() => {
    // Only set a timeout if the dialog is actually open
    if (!open) return; 
    
    const handler = setTimeout(() => {
        saveDraft();
    }, 1000); // Debounce: save 1 second after last change

    return () => {
      clearTimeout(handler);
    };
  }, [name, goalRaw, description, saveDraft, open]);


  // --- Handlers & Callbacks ---

  const handleImageChange = useCallback((file) => {
    setCampaignImageFile(file);
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  // Custom reset for successful submission
  const handleSuccessClose = useCallback(() => {
    setOpen(false);
    setName("");
    setGoal("");
    setGoalRaw("");
    setDescription("");
    setCampaignImageFile(null);
    setIsSubmitting(false);
    clearDraft(); // Clear the draft upon successful submission!
  }, [clearDraft]);

  // Handle dialog closing (including backdrop click/Escape key)
  const handleCloseDialog = useCallback(() => {
    // Before closing, save the current state as a draft
    saveDraft(); 
    setOpen(false);
  }, [saveDraft]);

  // format goal as user types
  const handleGoalChange = (e) => {
    const rawValue = e.target.value.replace(/,/g, "").replace(/\D/g, "");
    setGoalRaw(rawValue);
    setGoal(formatNumberWithCommas(rawValue));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (!name.trim() || !goalRaw.trim() || !description.trim()) {
      toast.error("Please fill in campaign name, goal, and description.");
      return;
    }
    if (!campaignImageFile) {
      toast.error("Please upload a campaign image.");
      return;
    }

    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("name", name);
    formData.append("goal", goalRaw); // send raw number without commas
    formData.append("description", description);
    formData.append("image", campaignImageFile);

    try {
      const response = await fetch(`${baseURL}/user/campaigns`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to create campaign.");
      }

      toast.success("Campaign created successfully!");
      handleSuccessClose(); 
    } catch (error) {
      console.error("Submission error:", error);
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Card
        sx={{
          p: 4,
          borderRadius: 2,
          boxShadow: 0,
          bgcolor: "#fff",
          border: `1px solid ${COLORS.border}`,
          textAlign: "left",
          mx: "auto",
        }}
      >
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, fontSize: "1.1rem" }}>
          Start a Fundraising Campaign
        </Typography>
        <Typography variant="body2" sx={{ color: "#666", mb: 2, fontSize: "0.9rem" }}>
          Create a campaign to raise funds for a cause you care about. Set your goal, add a compelling story, and start receiving donations.
        </Typography>
        <Button
          variant="contained"
          size="small"
          sx={{
            textTransform: "none",
            backgroundColor: COLORS.primary,
            borderRadius: "8px",
            fontWeight: "bold",
            fontSize: "0.9rem",
            boxShadow: 0,
            px: 2,
            py: 1,
            "&:hover": { boxShadow: 0 },
          }}
          onClick={() => setOpen(true)}
          startIcon={<FaPlusCircle />}
        >
          Add Campaign
        </Button>
      </Card>

      <Dialog open={open} onClose={handleCloseDialog} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontSize: "1.3rem", fontWeight: "bold", textAlign: "left" }}>
          Create a Fundraising Campaign
          {/* Button to explicitly clear the draft - Visible if content exists */}
          {(name || goalRaw || description) && (
              <Button 
                  onClick={clearDraft} 
                  sx={{ 
                      float: 'right', 
                      color: COLORS.danger, 
                      textTransform: 'none',
                      fontWeight: 'normal',
                      fontSize: '0.8rem' 
                  }}
                  startIcon={<FaTrashAlt size={12} />} 
                  disabled={isSubmitting}
              >
                  Delete Draft
              </Button>
          )}
        </DialogTitle>
        <Divider />

        <DialogContent sx={{ py: 2 }}>
          <TextField
            fullWidth
            label="Campaign Name"
            variant="outlined"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
            }}
            required
            disabled={isSubmitting}
          />
          <Typography sx={{ fontSize: "0.85rem", color: "#666", mb: 2 }}>
            Enter the name of your campaign. This will appear as the title on the website.
          </Typography>

          {/* Live formatting of goal amount */}
          <TextField
            fullWidth
            label="Goal Amount (UGX)"
            type="text" // must be text to allow commas
            variant="outlined"
            value={goal}
            onChange={handleGoalChange}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
            }}
            required
            disabled={isSubmitting}
          />
          <Typography sx={{ fontSize: "0.85rem", color: "#666", mb: 2 }}>
            Specify the goal amount in UGX. This amount will be shown as your fundraising target.
          </Typography>

          <div data-color-mode="light" style={{ marginBottom: "16px" }}>
            <MDEditor value={description} onChange={(value) => setDescription(value)} height={200} />
          </div>

          <Typography sx={{ fontSize: "0.85rem", color: "#666", mb: 2 }}>
            Write a brief description of your campaign. This will help potential donors understand what your campaign is about.
          </Typography>

          <ImageUploader onImageChange={handleImageChange} disabled={isSubmitting} />
          <Typography sx={{ fontSize: "0.85rem", color: "#666", mt: 2 }}>
            The image you upload will be displayed on your campaign. **(Note: The image is not saved in the draft and must be re-uploaded on reload.)**
          </Typography>
        </DialogContent>

        <Divider />
        <DialogActions>
          <Button onClick={handleCloseDialog} sx={{ textTransform: "none", color: COLORS.danger }} disabled={isSubmitting}>
            Cancel (Draft Saved)
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{ textTransform: "none", backgroundColor: COLORS.primary }}
            disabled={isSubmitting || !campaignImageFile}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isSubmitting ? "Creating..." : "Create Campaign"}
          </Button>
        </DialogActions>
      </Dialog>

      <SnackbarAlert open={snackbar.open} message={snackbar.message} severity={snackbar.severity} handleClose={handleCloseSnackbar} />
    </>
  );
};

export default AddCampaignCard;